// ═══════════════════════════════════════════════════════════════
//  affectation.js — résolution « mes chantiers » pour le module Tâches
//
//  🔴 PIÈGE D'IDENTITÉ (ticket §2.2). Ce fichier travaille EXCLUSIVEMENT en
//  ressourceId (= `utilisateurs/{id}`, ex. "Bilardo") : c'est la clé des
//  tableaux d'affectation d'un chantier (conducteurId, chefChantierIds[],
//  monteurIds[], artisanIds[]) ET de `planningCreneaux.ressourceId`.
//  Il ne voit JAMAIS un uid Firebase Auth. Les requêtes `taches`
//  (ownerUid / assigneUid) sont en uid et vivent dans useTachesModule.js.
//  Ne jamais comparer les deux — ils ne se ressemblent même pas, ce qui rend
//  la confusion silencieuse : une comparaison croisée renvoie `false` sans
//  jamais lever.
//
//  ⚠️ CE FICHIER N'IMPORTE RIEN, ET C'EST VOLONTAIRE.
//  Le prédicat « ce chantier est-il à moi ? » n'est PAS réécrit ici : c'est
//  `isMyChantier` de ../planning/planningModel.js, réutilisé tel quel, mais
//  INJECTÉ par l'appelant (useTachesModule.js) au lieu d'être importé.
//  Raison : planningModel importe `../avancement/avancementTasks` par un
//  specifier SANS extension, que Node ESM ne résout pas. L'importer ici
//  rendrait ce fichier — et donc affectation.test.js — inexécutable en `node`
//  pur, qui est la convention de test du repo (cf. planningModel.test.js, qui
//  recopie ses helpers faute de pouvoir les importer). L'injection garde le
//  helper UNIQUE côté application et la logique d'union RÉELLEMENT testée.
//
//  Deux sources, en UNION (pas en intersection) :
//   (1) la fiche chantier, déjà en mémoire via DataContext → 0 lecture ;
//   (2) le planning : un créneau `planningCreneaux` à mon ressourceId.
//  (2) est INDISPENSABLE : au 31/08/2026, 14 chantiers actifs sur 23 n'ont
//  aucun monteur renseigné dans leur fiche. Sans le planning, ces monteurs
//  ne verraient aucune tâche de chantier.
// ═══════════════════════════════════════════════════════════════

// Fenêtre de lecture du planning : 90 jours en arrière. Un monteur reste
// « affecté » à un chantier qu'il a quitté il y a moins de 3 mois — assez
// large pour couvrir un arrêt/congé, assez court pour ne pas ramener tout
// l'historique de créneaux.
export const FENETRE_CRENEAUX_JOURS = 90;

// Plafond dur de l'opérateur `in` de Firestore. Au-delà, on découpe et on
// dédoublonne côté client (calque de l'union qa/qb de ValidationAvancement).
export const MAX_IN_FIRESTORE = 30;

// Rien d'autre que ces deux clés ne désigne « moi » côté chantier/planning.
// `_id` = id du doc utilisateurs (posé par DataContext.mapDocs), `id` = champ
// data du même doc : les deux coexistent en base, on matche les deux.
export function ressourceIdsOf(user) {
  return [user?._id, user?.id].filter(Boolean).map(String);
}

// Identifiant de chantier utilisé PARTOUT ailleurs (planningCreneaux.chantierId,
// taches.chantierId) : le numéro Esabora 6 chiffres, qui est AUSSI l'id du doc
// `chantiers`. Vérifié en base le 02/09/2026 (chantiers/001374 → num "001374",
// planningCreneaux.chantierId "002216").
export function chantierIdOf(c) {
  const v = c?.num || c?._id || c?.id || null;
  return v ? String(v) : null;
}

// Date pivot de la fenêtre planning, au format YYYY-MM-DD (même format que
// `planningCreneaux.date`, comparable lexicographiquement).
// `refIso` : jour de référence YYYY-MM-DD (défaut : aujourd'hui, lu à l'appel).
export function dateDebutFenetre(refIso, jours = FENETRE_CRENEAUX_JOURS) {
  const base = typeof refIso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(refIso)
    ? new Date(`${refIso}T00:00:00Z`)
    : new Date();
  // UTC : on ne manipule qu'un décalage de jours, aucun fuseau n'entre en jeu
  // et le résultat reste stable quel que soit l'endroit où tourne le test.
  const d = new Date(Date.UTC(
    base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate(),
  ));
  d.setUTCDate(d.getUTCDate() - Math.max(0, Number(jours) || 0));
  return d.toISOString().slice(0, 10);
}

// Source (2) — chantiers vus dans MES créneaux de planning.
// Un créneau porte un `chantierId` de tête ET, depuis le lot multi-tâches, un
// tableau `taches[]` dont chaque entrée a SON propre chantierId : un créneau
// peut donc couvrir plusieurs chantiers. On ramasse les deux.
// Le filtre `ressourceId` est déjà posé côté requête ; on le re-vérifie ici
// parce qu'un créneau « pool » a `ressourceId: null` (vérifié en base) et
// n'appartient à personne.
export function chantierIdsDepuisCreneaux(creneaux, ressourceIds) {
  const miens = new Set((ressourceIds || []).filter(Boolean).map(String));
  const ids = new Set();
  for (const cr of creneaux || []) {
    if (!cr || cr.ressourceId == null) continue;
    if (!miens.has(String(cr.ressourceId))) continue;
    if (cr.chantierId) ids.add(String(cr.chantierId));
    const taches = Array.isArray(cr.taches) ? cr.taches : [];
    for (const t of taches) {
      if (t?.chantierId) ids.add(String(t.chantierId));
    }
  }
  return ids;
}

// Source (1) — chantiers dont la FICHE me nomme.
// `estAffecte` est `isMyChantier(user, chantier)` — injecté, jamais réécrit
// (voir l'en-tête). Aucune lecture Firestore : `chantiers` vient du DataContext.
export function chantierIdsDepuisFiches(chantiers, user, estAffecte) {
  const ids = new Set();
  for (const c of chantiers || []) {
    if (!estAffecte(user, c)) continue;
    const id = chantierIdOf(c);
    if (id) ids.add(id);
  }
  return ids;
}

/**
 * Résolution complète de « mes chantiers ».
 *
 * @param {object[]} chantiers    docs `chantiers` du DataContext (lecture seule)
 * @param {object[]} creneaux     docs `planningCreneaux` déjà lus (getDocs ponctuel)
 * @param {object}   user         utilisateur courant (porte _id / id)
 * @param {string|false} scope    can(user,"taches","view",rolesConfig)
 * @param {function} estAffecte   isMyChantier de planningModel — OBLIGATOIRE
 *
 * @returns {{ids:string[], idsFiches:string[], idsPlanning:string[], tous:boolean}}
 *   `ids` trié, dédoublonné. `tous` = true quand le scope court-circuite.
 *
 * Scope `"all"` (Direction / Admin / Assistante) → TOUS les chantiers, sans
 * consulter le planning. Scope faux (aucun droit) → liste vide : l'écran
 * n'émettra alors aucune requête B, ce qui est le comportement voulu.
 * Tout autre scope (`own_chantiers`, `own_items`) → union (1) ∪ (2).
 */
export function mesChantierIds({ chantiers, creneaux, user, scope, estAffecte }) {
  if (typeof estAffecte !== "function") {
    // Erreur de programmation, pas d'erreur d'exécution métier : un défaut
    // silencieux (« false partout ») viderait la source (1) sans aucun signal.
    throw new Error(
      "affectation.mesChantierIds : `estAffecte` (isMyChantier) est obligatoire — voir l'en-tête du fichier.",
    );
  }

  const liste = Array.isArray(chantiers) ? chantiers : [];

  if (scope === "all") {
    const tousIds = [...new Set(liste.map(chantierIdOf).filter(Boolean))].sort();
    return { ids: tousIds, idsFiches: tousIds, idsPlanning: [], tous: true };
  }
  if (!scope) {
    return { ids: [], idsFiches: [], idsPlanning: [], tous: false };
  }

  const fiches = chantierIdsDepuisFiches(liste, user, estAffecte);
  const planning = chantierIdsDepuisCreneaux(creneaux, ressourceIdsOf(user));
  const union = new Set([...fiches, ...planning]);

  return {
    ids: [...union].sort(),
    idsFiches: [...fiches].sort(),
    // Ce que SEUL le planning apporte : c'est le chiffre qui justifie la
    // source (2), utile en diagnostic quand un monteur « ne voit rien ».
    idsPlanning: [...planning].filter((id) => !fiches.has(id)).sort(),
    tous: false,
  };
}

// Découpe une liste d'ids en paquets consommables par `where(..., "in", ...)`.
// Retourne [] pour une liste vide → l'appelant n'émet aucune requête.
export function decouperIn(ids, taille = MAX_IN_FIRESTORE) {
  const t = Math.max(1, Number(taille) || MAX_IN_FIRESTORE);
  const src = (ids || []).filter(Boolean);
  const out = [];
  for (let i = 0; i < src.length; i += t) out.push(src.slice(i, i + t));
  return out;
}
